import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Film, Download, Loader2, Play, Coins, Sparkles, Check, X, AlertTriangle } from "lucide-react";

// Cost: 1.25 credits/sec for sora-2, 2.5/sec for pro.
// 4s=5/10, 8s=10/20, 12s=15/30
const computeVideoCost = (duration, model) => {
  const base = duration * 1.25;
  return Math.floor(model === "sora-2-pro" ? base * 2 : base);
};

const SIZES = [
  { key: "1280x720", label: "Paysage HD", desc: "16:9 réseaux sociaux / YouTube" },
  { key: "1024x1792", label: "Portrait", desc: "9:16 TikTok / Stories" },
  { key: "1024x1024", label: "Carré", desc: "1:1 Instagram feed" },
  { key: "1792x1024", label: "Cinéma", desc: "Ultra wide" },
];
const DURATIONS = [4, 8, 12];

const EXAMPLE_PROMPTS = [
  "Plan rapproché d'un café latte versé en slow motion, vapeur dorée, ambiance bistrot parisien",
  "Une voiture de luxe noire roulant sous la pluie nocturne, néons or, reflets cinéma",
  "Atelier d'artisan bijoutier, gros plan sur des mains travaillant l'or, lumière chaude",
];

export default function VideoStudio() {
  const { user, checkAuth } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("1280x720");
  const [duration, setDuration] = useState(4);
  const [model, setModel] = useState("sora-2");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [progressSec, setProgressSec] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const credits = user?.credits ?? 0;
  const cost = computeVideoCost(duration, model);
  const canAfford = credits >= cost;

  // Prompt enhancer (free LLM-powered analyser/rewriter)
  const [enhancing, setEnhancing] = useState(false);
  const [enhancement, setEnhancement] = useState(null); // {original, enhanced_prompt, changes, warnings, risk_level}

  const enhancePrompt = async () => {
    if (!prompt.trim()) { toast.error("Saisissez d'abord un prompt à optimiser"); return; }
    setEnhancing(true); setEnhancement(null);
    try {
      const { data } = await api.post("/video/enhance-prompt", {
        prompt, duration, style: "cinematic",
      });
      setEnhancement(data);
      const riskMsg = data.risk_level === "high"
        ? "⚠️ Prompt à risque — version sécurisée prête"
        : data.risk_level === "medium"
        ? "Prompt optimisé — quelques ajustements"
        : "Prompt optimisé !";
      toast.success(riskMsg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur optimisation");
    } finally { setEnhancing(false); }
  };

  const acceptEnhancement = () => {
    if (!enhancement?.enhanced_prompt) return;
    setPrompt(enhancement.enhanced_prompt);
    setEnhancement(null);
    toast.success("Prompt remplacé");
  };

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startPolling = (projectId) => {
    // Elapsed-time counter (purely UI)
    timerRef.current = setInterval(() => setProgressSec(s => s + 1), 1000);
    // Status polling every 8s
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}`);
        if (data.status === "completed" && data.data_url) {
          stopPolling();
          setResult(data);
          setLoading(false);
          toast.success("Vidéo prête !");
          checkAuth(); // refresh credits
        } else if (data.status === "failed") {
          stopPolling();
          setLoading(false);
          const refundedMsg = data.refunded ? " (crédits remboursés)" : "";
          toast.error((data.error || "Génération échouée") + refundedMsg);
          checkAuth(); // refund may have applied
        }
      } catch (e) {
        // Transient errors during polling are ignored; the task continues server-side.
        console.warn("poll error:", e?.message);
      }
    }, 8000);
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Décrivez votre vidéo"); return; }
    if (!canAfford) {
      toast.error(`Crédits insuffisants (${credits}/${cost} requis)`);
      return;
    }
    stopPolling();
    setLoading(true); setResult(null); setProgressSec(0);
    try {
      const { data } = await api.post("/video/generate", { prompt, size, duration, model });
      if (!data?.project_id) {
        throw new Error("Pas de project_id renvoyé");
      }
      const charged = data.credits_charged || 0;
      toast.info(charged > 0
        ? `Sora travaille — ${charged} crédits débités, 2 à 5 min...`
        : "Sora travaille — patientez 2 à 5 minutes...");
      checkAuth(); // immediate balance refresh
      startPolling(data.project_id);
    } catch (e) {
      setLoading(false);
      const status = e?.response?.status;
      if (status === 402) {
        toast.error(e.response.data?.detail || "Crédits insuffisants");
      } else {
        toast.error(e.response?.data?.detail || "Erreur démarrage génération");
      }
    }
  };

  const download = () => {
    if (!result?.data_url) return;
    const a = document.createElement("a");
    a.href = result.data_url;
    a.download = `xookpub-video-${Date.now()}.mp4`;
    a.click();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-mono">Studio · Vidéo IA</span>
          <h1 className="mt-2 text-4xl font-heading tracking-tighter">
            <span className="gold-text">Sora 2</span> compose votre vidéo publicitaire
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 text-sm">
            <Coins className="w-4 h-4 text-gold" />
            <span data-testid="video-user-credits" className={canAfford ? "text-gold font-semibold" : "text-red-400 font-semibold"}>
              {credits} crédits
            </span>
            <span className="text-white/40">·</span>
            <span data-testid="video-cost" className="text-white/60">{cost} pour {duration}s</span>
          </div>
          {!canAfford && (
            <Link to="/pricing" data-testid="video-buy-credits" className="ghost-btn !py-2 !px-3 text-xs">Recharger</Link>
          )}
          <a href="/studio/sequence" data-testid="goto-sequence" className="ghost-btn !py-2 !px-4 text-sm">
            Multi-scènes →
          </a>
          <div className="text-xs text-white/50 px-3 py-2 rounded-full border border-white/10">
            ⏱️ Génération : 2 à 5 min
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-5">
          <div className="studio-card p-5">
            <div className="flex items-center justify-between">
              <label className="label-mono">Scénario / Prompt</label>
              <button
                data-testid="video-enhance-btn"
                onClick={enhancePrompt}
                disabled={enhancing || !prompt.trim()}
                className="text-xs px-3 py-1.5 rounded-full border border-gold/40 text-gold hover:bg-gold/10 transition flex items-center gap-1.5 disabled:opacity-40"
                title="Reformule votre prompt en anglais cinéma + supprime les risques de modération (gratuit)"
              >
                {enhancing
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyse...</>
                  : <><Sparkles className="w-3.5 h-3.5" /> Optimiser le prompt</>}
              </button>
            </div>
            <textarea
              data-testid="video-prompt"
              value={prompt}
              onChange={(e)=>setPrompt(e.target.value)}
              placeholder="Décrivez la scène : action, ambiance, plan caméra, éclairage, ton..."
              className="mt-2 w-full h-40 bg-obsidian-light border border-white/10 rounded-lg p-3 outline-none focus:border-gold/50 text-sm resize-none"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button key={i} data-testid={`video-ex-${i}`} onClick={()=>setPrompt(ex)} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-gold/10 hover:text-gold border border-white/10 text-white/60 transition">
                  {ex.slice(0, 38)}...
                </button>
              ))}
            </div>

            {/* Enhancement preview card */}
            {enhancement && (
              <div data-testid="enhancement-preview" className="mt-4 p-4 rounded-lg border border-gold/30 bg-gradient-to-br from-gold/5 to-transparent">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-gold" />
                    <span className="label-mono text-gold">Prompt optimisé</span>
                    {enhancement.risk_level === "high" && (
                      <span className="text-[10px] uppercase tracking-widest text-red-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> risque élevé
                      </span>
                    )}
                    {enhancement.risk_level === "medium" && (
                      <span className="text-[10px] uppercase tracking-widest text-amber-400">risque moyen</span>
                    )}
                  </div>
                  <button data-testid="enhancement-close" onClick={()=>setEnhancement(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                </div>

                <div className="bg-obsidian-light/60 rounded p-3 text-sm leading-relaxed text-white/90" data-testid="enhanced-prompt-text">
                  {enhancement.enhanced_prompt}
                </div>

                {enhancement.changes?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-white/60">
                    {enhancement.changes.map((c, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-3 h-3 text-gold mt-0.5 flex-shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {enhancement.warnings?.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-amber-400/90">
                    {enhancement.warnings.map((w, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-4 flex gap-2">
                  <button data-testid="enhancement-accept" onClick={acceptEnhancement} className="gold-btn !py-2 !px-4 text-sm flex-1">
                    <Check className="w-4 h-4" /> Utiliser cette version
                  </button>
                  <button data-testid="enhancement-reject" onClick={()=>setEnhancement(null)} className="ghost-btn !py-2 !px-4 text-sm">
                    Garder l&apos;original
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="studio-card p-5">
            <label className="label-mono">Format</label>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {SIZES.map(s => (
                <button key={s.key} data-testid={`size-${s.key}`} onClick={()=>setSize(s.key)}
                  className={`p-3 rounded-lg border text-left ${size===s.key ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}>
                  <div className="font-heading text-sm">{s.label}</div>
                  <div className="text-[10px] text-white/50 mt-0.5 uppercase tracking-widest">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="studio-card p-5">
            <label className="label-mono">Durée</label>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {DURATIONS.map(d => (
                <button key={d} data-testid={`duration-${d}`} onClick={()=>setDuration(d)}
                  className={`py-3 rounded-lg border ${duration===d ? "border-gold bg-gold/10 text-gold" : "border-white/10 hover:border-white/30"}`}>
                  {d}s
                </button>
              ))}
            </div>

            <div className="mt-5">
              <label className="label-mono">Modèle</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button data-testid="model-sora-2" onClick={()=>setModel("sora-2")} className={`py-2.5 rounded-lg border text-sm ${model==="sora-2"?"border-gold bg-gold/10 text-gold":"border-white/10"}`}>Sora 2 (rapide)</button>
                <button data-testid="model-sora-2-pro" onClick={()=>setModel("sora-2-pro")} className={`py-2.5 rounded-lg border text-sm ${model==="sora-2-pro"?"border-gold bg-gold/10 text-gold":"border-white/10"}`}>Sora 2 Pro (premium)</button>
              </div>
            </div>
          </div>

          <button data-testid="video-generate-btn" onClick={generate} disabled={loading || !canAfford}
            className="gold-btn w-full !py-4 text-base disabled:opacity-50">
            {loading
              ? <><Loader2 className="w-5 h-5 animate-spin" /> Sora travaille...</>
              : <><Film className="w-5 h-5" /> Générer ma vidéo · {cost} crédits</>}
          </button>
        </div>

        <div className="lg:col-span-7">
          <div className="studio-card p-5 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <span className="label-mono">Aperçu</span>
              {result?.data_url && (
                <button data-testid="video-download" onClick={download} className="ghost-btn !py-2 !px-4 text-sm">
                  <Download className="w-4 h-4" /> Télécharger MP4
                </button>
              )}
            </div>
            <div className="aspect-video bg-obsidian rounded-xl border border-white/5 overflow-hidden flex items-center justify-center">
              {loading ? (
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto rounded-full border-2 border-gold border-t-transparent animate-spin" />
                  <div className="mt-4 label-mono" data-testid="video-loading-label">Sora compose votre vidéo...</div>
                  <div className="mt-1 text-xs text-white/40">
                    {Math.floor(progressSec / 60)}min {progressSec % 60}s écoulés · 2 à 5 min en général
                  </div>
                  <div className="mt-2 text-[11px] text-white/30 max-w-xs mx-auto leading-relaxed">
                    Vous pouvez naviguer ailleurs et revenir — la génération continue en arrière-plan.
                  </div>
                </div>
              ) : result?.data_url ? (
                <video src={result.data_url} controls className="w-full h-full object-contain" data-testid="video-result-player" />
              ) : (
                <div className="text-center text-white/30">
                  <Play className="w-12 h-12 mx-auto" />
                  <div className="mt-3 text-sm">Votre vidéo apparaîtra ici</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
